import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Icon,
  ScrollArea,
  Spinner,
  Text,
  WidgetCard,
  WidgetEmpty,
  WidgetGrid,
  diffMoves,
  moveItem,
  packLayout,
  useGridHistoryHotkeys,
  useWidgetBoardHistory,
  useWidgetRegistry,
  type GridLayoutItem,
  type GridRect,
  type WidgetCardSlotProps,
  type WidgetConfig,
  type WidgetGridAreaAdd,
  type WidgetInstance,
  type WidgetMove,
} from "@salestrak/ui";
import { AddWidgetDialog } from "#app/widgets/dashboards/settings/add-widget-dialog";
import {
  DashboardPageSettingsSection,
  useDashboardPageSettings,
  type DashboardPageSettings,
} from "#app/widgets/dashboards/settings/dashboard-page-settings";
import { DashboardTemplateChooser } from "#app/widgets/dashboards/settings/dashboard-template-chooser";
import { DashboardStartOverDialog } from "#app/widgets/dashboards/settings/dashboard-start-over-dialog";
import {
  templateInstances,
  type DashboardTemplate,
} from "#app/widgets/dashboards/settings/dashboard-templates";
import { ManageWidgetsPanel } from "#app/widgets/dashboards/settings/manage-widgets-panel";
import { WidgetDetailDialog } from "#app/widgets/dashboards/widget-detail-dialog";
import { WidgetDrilldownProvider, type WidgetDataSlice } from "#app/widgets/dashboards/widget-drilldown";
import { GRID_COLUMNS, useWidgetView } from "#app/widgets/dashboards/use-widget-view";
import { useWidgetViewAccess } from "#app/widgets/dashboards/use-widget-view-access";
import { useManagedBoardMaintenance } from "#app/widgets/dashboards/use-managed-board-maintenance";
import {
  HALF_FULL_WIDTH_OPTIONS,
  managedVirtualInstances,
  snapHalfFullMoves,
  usesHalfFullWidths,
} from "#app/widgets/dashboards/managed-surface";
import { managedSeed, type ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import { OwnershipPill } from "#app/widgets/dashboards/ownership-pill";
import {
  DashboardMastheadChrome,
  FreeToolbarChrome,
  ManagedToolbarChrome,
} from "#app/widgets/dashboards/surface-chrome";
import { resolveWidgetViewMode, type WidgetViewLens } from "#app/widgets/dashboards/widget-view-mode";
import { parsePersonalSnapshot } from "#app/widgets/dashboards/widget-view-snapshot";
import { DashboardScopeProvider } from "#app/widgets/dashboards";
import {
  useDashboardScopeState,
  type DashboardEntityScope,
} from "#app/widgets/dashboards/scope/use-dashboard-scope-state";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { scopeEntityId, scopeWhere, type IssueScope } from "#app/lib/filters/issue-scope";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { combineWhereConditions } from "#app/lib/query-utils";
import { useAnalytics, AnalyticsContext, WidgetViewEvent } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

/**
 * The `Dashboard` host: the library `WidgetGrid` + the surrounding chrome
 * (`surface-chrome.tsx` — one of three headers per surface, empty states) +
 * the app-owned `AddWidgetDialog` gallery and the `WidgetDetailDialog` editor
 * (whose Settings drawer is THE widget settings surface — the gear and
 * Maximize both open it), over the widget view store (`useWidgetView`), with
 * every widget self-fetching through the ambient dashboard scope. The host
 * also provides the drilldown channel: a chart body publishes a clicked
 * slice through `useWidgetDrilldown`, and the detail dialog opens on its
 * Data tab filtered to it. Hosted by `/my-work` (member-scoped) and by the
 * per-workflow / per-team Dashboard tabs (entity-scoped).
 *
 * This is the DASHBOARD domain's host — it owns its own chrome and copy so
 * the library kit stays domain-free (a future domain can compose
 * `WidgetGrid` with its own chrome the same way).
 *
 * Widgets persist through the core GraphQL widgets API keyed by `(workspace,
 * view)`. Who may edit is the OWNERSHIP model (`useWidgetViewAccess` — the
 * same shared grammar the server enforces), and it is ONE model for every
 * view: a view has a shared/default board, ANY viewer may hold a personal
 * fork of it (a private settings snapshot that never writes widget rows),
 * and a per-view LENS (persisted in localStorage, resolved by
 * `resolveWidgetViewMode`) picks which of the two this surface shows. The
 * only permission question is who edits the SHARED board: `widgets.manage`
 * holders and that team's lead, whose template edits STAGE behind "Save for
 * the team"; everyone else sees it read-only. My Work is the private-owner
 * special case — the owner edits their board directly (autosave, no fork)
 * and can reset it back to the catalog's default seed. The ownership pill
 * (`ownership-pill.tsx`) carries every state's explanation and actions —
 * Personalize, the lens switch, Reset, Save/Discard — for every viewer.
 *
 * The `IssueScope` pins the dashboard to one workflow, team, or member: its
 * `scopeWhere` predicate is AND-merged into every widget's query (via
 * `useScopedWhere`), and the dashboard gets its own view key per entity.
 * Feature-flag gating lives in the host route, not here.
 * Pass `surfaceVariant` for a distinct persisted view under the same scope.
 *
 * Pass `managedCatalog` to make this a **managed** surface (My Work, Team
 * Overview): a fixed, curated set of predefined widgets the viewer only
 * toggles on/off — NOT a free add-any-widget gallery. The designed x/y/w/h
 * are the starting layout; editors can rearrange/resize, and the toggle
 * panel remains the only add/remove control. Every view's default board —
 * managed default-on set or a free dashboard's default template — is seeded
 * SERVER-SIDE on the view's first read (core's widget-view-defaults.ts), so
 * boards exist before any client logic runs. A titled managed surface gets
 * the page masthead (`title` + Manage widgets); a titleless managed surface
 * gets a compact date-range + Manage-widgets toolbar instead.
 */

function greetingLabel(name?: string | null) {
  const hour = new Date().getHours();
  const period = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  const firstName = name?.trim().split(/\s+/)[0];

  return firstName ? `Good ${period}, ${firstName}` : `Good ${period}`;
}

function todayDateLabel() {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date());
}

export type DashboardProps = Omittable<{
  scope: IssueScope;
  /** Optional masthead breadcrumb label for managed surfaces (e.g. "My Work"). */
  title?: string;
  surfaceVariant?: string;
  managedCatalog?: ManagedWidget[];
  /** Enables My Work-only page chrome settings: greeting text and settings gear. */
  pageSettings?: boolean;
}>;

type DashboardInnerProps = Omit<DashboardProps, "pageSettings"> & Omittable<{
  pageSettingsState?: DashboardPageSettings;
  onPageSettingsUpdate?: (patch: Partial<DashboardPageSettings>) => void;
}>;

export function Dashboard({ pageSettings, ...props }: DashboardProps) {
  if (pageSettings) return <DashboardWithPageSettings {...props} />;
  return <DashboardInner {...props} />;
}

function DashboardWithPageSettings(props: Omit<DashboardProps, "pageSettings">) {
  const { state, update } = useDashboardPageSettings();
  return <DashboardInner {...props} pageSettingsState={state} onPageSettingsUpdate={update} />;
}

function DashboardInner({
  scope,
  title,
  surfaceVariant,
  managedCatalog,
  pageSettingsState,
  onPageSettingsUpdate,
}: DashboardInnerProps) {
  const { track } = useAnalytics();
  const { currentUser } = useAppData();
  // Stamp every widget event with the host's scope so the dataset is mineable:
  // "member" (my-work), or workflow/team for the Dashboard tabs.
  const widgetScope = scope.kind;
  const entityId = scopeEntityId(scope);

  // ONE envelope for every widget event — the WidgetView context + the host's
  // scope/entity fold in here, so call sites carry only their own fields.
  const trackWidget = useCallback(
    (event: WidgetViewEvent, data?: Record<string, unknown>) =>
      track({
        context: AnalyticsContext.WidgetView,
        event,
        data: { scope: widgetScope, entityId, ...data },
      }),
    [track, widgetScope, entityId],
  );

  // The viewer's relationship to THIS view (the shared ownership grammar the
  // server guard also enforces): `canWrite` = the server accepts canonical
  // writes; `tier` + the lens below pick the store mode and the pill.
  const access = useWidgetViewAccess(scope, surfaceVariant);
  const { teamMap } = useWorkspaceData();
  const registry = useWidgetRegistry();

  // The shared quick-filter panel — mounted by the workflow/team hosts (which
  // wrap their tabs in an `IssueFilterProvider`), absent on `/my-work`. Its
  // chip lives in the toolbar; its applied `where` AND-merges into the entity
  // scope below so the panel actually narrows every widget's data.
  const issueFilters = useIssueFiltersOptional();
  const filterWhere = issueFilters?.filters.where;
  const filterWhereKey = filterWhere ? JSON.stringify(filterWhere) : "";

  // Memoize on primitive deps (+ a stable serialization of the panel's
  // `where`) so the published DashboardScope keeps a stable identity across
  // renders (the context value otherwise re-subscribes every widget).
  const entityScope = useMemo<DashboardEntityScope | null>(() => {
    const conditions = [scopeWhere(scope), filterWhere].filter((c): c is Record<string, unknown> => Boolean(c));
    return {
      entity: { type: scope.kind, id: scopeEntityId(scope) },
      entityWhere: combineWhereConditions(conditions) ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeEntityId(scope), filterWhereKey]);

  // The toolbar-driven data window every widget reports on. My Work
  // (member-scoped) is a personal home that must surface EVERYTHING assigned to
  // the viewer — and its titled masthead has no date-range control — so it opens
  // on the unbounded `all-time` window instead of the default this-week preset.
  // Widgets that want a narrower window (e.g. "Newly closed this week") carry
  // their own per-widget date override. Entity-scoped dashboards keep the
  // toolbar's default preset.
  const scopeState = useDashboardScopeState(
    entityScope,
    scope.kind === "member" ? { key: "all-time" } : undefined,
  );

  // Personalize arms edit mode on a shared view; the fork itself persists on
  // the first real change. Once a fork exists the view is personal whether or
  // not this session pressed the button.
  const [personalizing, setPersonalizing] = useState(false);

  // The viewer's fork + lens (any shared view). The store only reads the
  // fork once the mode IS personal, so the host needs its own read to know a
  // fork exists at all — `useUserSetting`'s cache write-back keeps this
  // second consumer of the same key live with the store's fork writes and
  // Reset. The lens (which board the viewer is on) persists per view in
  // localStorage so a fork holder lands back on their fork after a reload.
  const fork = useUserSetting(`widget-view-personal:${access.view}`, parsePersonalSnapshot, {
    skip: access.tier !== "shared",
  });
  const hasFork = fork.value !== null;
  const { state: lensState, update: updateLens } = usePersistedState<{ lens: WidgetViewLens }>(
    `widget-view-lens:${access.view}`,
    { lens: "personal" },
  );

  // Where writes go: a shared view's template lens stages behind "Save for
  // the team" for writers (read-only for everyone else); the personal lens
  // writes the viewer's private settings fork; private views (My Work) keep
  // instant autosave — there is no blast radius.
  const mode = resolveWidgetViewMode({
    tier: access.tier,
    canWrite: access.canWrite,
    lens: lensState.lens,
    hasFork,
    personalizing,
  });
  const storeOpts = useMemo(() => ({ mode }), [mode]);
  const {
    widgets,
    initialLoading,
    addWidget,
    seedWidgets,
    duplicateWidget,
    replaceWidget,
    removeWidget,
    updateWidgetConfig,
    moveWidgets,
    restoreBoard,
    dirty,
    saveToTeam,
    discardChanges,
    saveAsPersonal,
    hasPersonalLayer,
    resetToDefault,
    direct,
  } = useWidgetView(access.view, storeOpts);

  // Which view key has completed its first load — feeds the initialLoading
  // gate above the JSX (first load = full-page spinner; later resets keep
  // the chrome).
  const hasLoadedViewRef = useRef<string | null>(null);

  const personalActive = mode === "personal" && (personalizing || hasPersonalLayer);
  // Effective edit — gates every edit affordance (masthead/toolbar buttons,
  // grid manage/arrange, hotkeys, empty states). Canonical-board maintenance
  // stays on `canMaintainCanonical` below.
  const canEdit = access.canWrite || personalActive;

  // Managed surfaces hide the per-widget manage chrome (no gear/rename/
  // duplicate/delete — the toggle panel is the only add/remove surface) but
  // stay arrangeable: `canArrange` lets `widgets.manage` holders drag-reorder
  // and resize, and moves persist through `moveWidgets` like any view.
  const isManaged = !!managedCatalog;
  const halfFullWidths = usesHalfFullWidths(managedCatalog);

  // Free dashboards are always editable for a permitted viewer — there is no
  // edit mode. `canManageWidgets` is the RIGHT, and it alone arms the board's
  // edit affordances; unsaved shared edits surface through the ownership
  // pill's dirty popover.
  const canManageWidgets = isManaged ? false : canEdit;
  const gridCanManage = canManageWidgets;

  // Canonical-board maintenance runs only against the CANONICAL board: never
  // for a personalizing viewer, and never while an editor's lens is on their
  // personal fork (the effects would inspect/write the fork's rows). It also
  // stands down while staged edits are dirty: the corrections write through to
  // the server, but the frozen staged baseline would keep the corrected rows —
  // Discard would then resurrect server-deleted widgets. Maintenance defers
  // until the editor saves or discards (the effects dep on this gate, so
  // dirty clearing re-runs them against the re-synced board).
  const canMaintainCanonical = access.canWrite && mode !== "personal" && !dirty;

  const [addOpen, setAddOpen] = useState(false);
  // Widget id pending replacement — the Add-Widget gallery doubles as the
  // Replace picker while this is set.
  const [replaceId, setReplaceId] = useState<string | null>(null);
  // The detail dialog's target, held as an ID and re-derived from the live list
  // so in-dialog edits (rename, config) reflect immediately instead of showing
  // a stale snapshot; the index drives the dialog's prev/next stepping.
  const [maximizedId, setMaximizedId] = useState<string | null>(null);
  // A clicked chart element's slice — set alongside `maximizedId` by the
  // drilldown channel, cleared on close/nav so it never leaks across widgets.
  const [dataSlice, setDataSlice] = useState<WidgetDataSlice | null>(null);
  // Bumped on every drilldown open so the detail dialog surfaces its Data tab —
  // even for an unfiltered open (the metric widget) where `dataSlice` stays null.
  // The plain Fullscreen affordance leaves this untouched, so it opens chart-only.
  const [dataRequestId, setDataRequestId] = useState(0);
  // Bumped when the gear is pressed so the detail dialog opens on its Settings
  // tab — the gear opens the same fullscreen editor as Maximize, pre-drilled to
  // settings. Plain Fullscreen leaves this untouched, so it opens chart-only.
  const [settingsRequestId, setSettingsRequestId] = useState(0);
  const drilldown = useMemo(
    () => ({
      openData: (instanceId: string, slice: WidgetDataSlice | null) => {
        setDataSlice(slice);
        setDataRequestId((n) => n + 1);
        setMaximizedId(instanceId);
      },
    }),
    [],
  );
  const maximizedIndex = useMemo(
    () => (maximizedId ? widgets.findIndex((i) => i.id === maximizedId) : -1),
    [widgets, maximizedId],
  );
  const maximizedInstance = maximizedIndex >= 0 ? at(widgets, maximizedIndex, "widget") : null;
  const onMaximize = useCallback((widget: WidgetInstance) => setMaximizedId(widget.id), []);

  const onMove = useCallback(
    (moves: WidgetMove[]) => {
      const committedMoves = halfFullWidths ? snapHalfFullMoves(widgets, moves) : moves;
      moveWidgets(committedMoves);
      trackWidget(WidgetViewEvent.Rearrange, { count: committedMoves.length });
    },
    [halfFullWidths, widgets, moveWidgets, trackWidget],
  );

  // Full-board undo/redo over the working copy (free dashboards — a managed
  // board's layout is catalog-designed, so it gets no history/Tidy). Grid
  // gestures persist through `onMove` and RECORD through the kit's
  // full-layout `onLayoutChange`; membership ops (add/duplicate/replace/
  // delete/seed) RECORD via `snapshot()` at their call sites; undo/redo/Tidy
  // commit through the store's mode-routed `restoreBoard`. The reset key
  // carries the MODE too: the editor's lens switch changes boards under the
  // same view key, and one board's snapshots must not restore onto the other.
  const layout = useMemo<GridLayoutItem[]>(
    () => widgets.map((i) => ({ id: i.id, x: i.x, y: i.y, w: i.w, h: i.h })),
    [widgets],
  );
  const history = useWidgetBoardHistory({
    board: widgets,
    commit: restoreBoard,
    resetKey: `${access.view}:${mode}`,
  });
  useGridHistoryHotkeys({
    undo: history.undo,
    redo: history.redo,
    enabled: canEdit && !isManaged,
  });
  const onTidy = useCallback(() => history.apply(packLayout(layout, GRID_COLUMNS)), [history, layout]);

  const onDelete = useCallback(
    (id: string) => {
      const type = widgets.find((i) => i.id === id)?.type;
      trackWidget(WidgetViewEvent.DeleteWidget, { type });
      history.snapshot();
      removeWidget(id);
    },
    [widgets, removeWidget, trackWidget, history],
  );

  // Marquee-to-add: a drawn rect opens the SAME Add-Widget gallery as the
  // toolbar button (never a parallel inline mini-picker) and places the pick
  // at the drawn cell AND size. The host holds the drawn rect so the grid
  // keeps it painted while the gallery is open; closing without a pick
  // clears it. Free dashboards only — a managed surface's catalog is fixed.
  const [marqueeArea, setMarqueeArea] = useState<GridRect | null>(null);
  const areaAdd = useMemo<WidgetGridAreaAdd>(
    () => ({
      pending: marqueeArea,
      onDraw: (area) => {
        setMarqueeArea(area);
        setAddOpen(true);
      },
    }),
    [marqueeArea],
  );

  const onConfigure = useCallback(
    (widget: WidgetInstance) => {
      trackWidget(WidgetViewEvent.ConfigureWidget, { type: widget.type });
      // The gear opens the fullscreen detail dialog with its Settings drawer
      // open — that dialog is the one and only widget editor.
      setSettingsRequestId((n) => n + 1);
      setMaximizedId(widget.id);
    },
    [trackWidget],
  );

  const onConfigChange = useCallback(
    (id: string, patch: WidgetConfig) => updateWidgetConfig(id, patch),
    [updateWidgetConfig],
  );

  const onDuplicate = useCallback(
    (widget: WidgetInstance) => {
      trackWidget(WidgetViewEvent.DuplicateWidget, { type: widget.type });
      history.snapshot();
      duplicateWidget(widget);
    },
    [duplicateWidget, trackWidget, history],
  );

  const onReplace = useCallback((widget: WidgetInstance) => setReplaceId(widget.id), []);

  // Managed widgets paint `elevated` — tiles floating over the sunken
  // board — and suppress Maximize per type (team-members owns its own
  // expansion UX). The slot props carry everything else — including the
  // width menu when the host wires grid-level `widthOptions`.
  const renderManagedCard = useCallback(
    (props: WidgetCardSlotProps) => (
      <WidgetCard
        {...props}
        surface="elevated"
        canvas="sunken"
        onMaximize={props.instance.type === "team-members" ? undefined : props.onMaximize}
      />
    ),
    [],
  );

  // Duplicate repair, the managed layout migration, and the gravity
  // self-heal — the canonical-board maintenance effects, in their required
  // order. (First-visit seeding is server-side: the widgets read lazily
  // seeds an uninitialized view's default board.)
  useManagedBoardMaintenance({
    managedCatalog,
    initialLoading,
    canMaintain: canMaintainCanonical,
    widgets,
    direct,
  });

  // Toggle a managed widget on or off. The managed board reflows under
  // GRAVITY like any other board: toggling off compacts the survivors up so
  // the hole closes, and toggling on re-enters at the widget's designed slot
  // settled through `moveItem` — identity when the slot is free, honest
  // sibling reflow (then compaction) when something now occupies it. Matched
  // by the stable `widgetKey` baked into each instance's config. Goes through
  // the PUBLIC store ops on purpose: they stage for editors and fork for
  // personalizing viewers. One mental model.
  const onToggleManaged = useCallback(
    (widget: ManagedWidget, enabled: boolean) => {
      if (enabled) {
        trackWidget(WidgetViewEvent.AddWidget, { type: widget.type });
        const seed = managedSeed(widget);
        const seedId = "__seed__";
        const settled = moveItem(
          [...layout, { id: seedId, x: seed.x, y: seed.y, w: seed.w, h: seed.h }],
          seedId,
          seed.x,
          seed.y,
          GRID_COLUMNS,
          "gravity",
        );
        const cell = settled.find((i) => i.id === seedId) ?? seed;
        // Siblings first, then the seed at its settled cell — both optimistic,
        // so the board opens the hole and the widget lands in it.
        const siblingMoves = diffMoves(
          layout,
          settled.filter((i) => i.id !== seedId),
        );
        if (siblingMoves.length > 0) moveWidgets(siblingMoves);
        seedWidgets([{ ...seed, x: cell.x, y: cell.y }]);
        return;
      }
      const match = widgets.find((i) => (i.config as { widgetKey?: string } | null)?.widgetKey === widget.widgetKey);
      if (!match) return;
      trackWidget(WidgetViewEvent.DeleteWidget, { type: widget.type });
      removeWidget(match.id);
    },
    [widgets, layout, seedWidgets, moveWidgets, removeWidget, trackWidget],
  );

  // Personalize: arm the fork. The canonical board always exists once read
  // (the server lazily seeds a view's defaults on its first read), so the
  // fork starts from real rows — no virtual-seed handoff. Managed surfaces
  // also open the toggle panel, since it is the managed edit surface.
  // Editors land on the personal lens so a stale persisted "template" choice
  // can't strand the fresh fork.
  const onPersonalize = useCallback(() => {
    trackWidget(WidgetViewEvent.Personalize);
    setPersonalizing(true);
    updateLens({ lens: "personal" });
    if (isManaged) setAddOpen(true);
  }, [trackWidget, updateLens, isManaged]);

  // Reset = re-seed the personal board FROM the shared layout — the viewer
  // STAYS on their personal lens. The store deletes the fork and the seeding
  // effect re-seeds the working copy from the canonical rows; keeping
  // `personalizing` armed means the next edit re-creates the fork rather
  // than bouncing the viewer over to the Shared board.
  const onResetPersonal = useCallback(() => {
    trackWidget(WidgetViewEvent.ResetPersonalLayout);
    resetToDefault();
    setPersonalizing(true);
  }, [trackWidget, resetToDefault]);

  const onSaveForTeam = useCallback(() => {
    trackWidget(WidgetViewEvent.SaveForTeam);
    void saveToTeam();
  }, [trackWidget, saveToTeam]);

  const onDiscardStaged = useCallback(() => {
    trackWidget(WidgetViewEvent.DiscardStagedChanges);
    discardChanges();
  }, [trackWidget, discardChanges]);

  // "Save for just me": the staged edits become the fork, the template
  // reverts, and the lens lands on the fork — the viewer keeps looking at
  // exactly the board they built.
  const onSaveAsPersonal = useCallback(() => {
    trackWidget(WidgetViewEvent.SaveAsPersonal);
    saveAsPersonal();
    setPersonalizing(true);
    updateLens({ lens: "personal" });
  }, [trackWidget, saveAsPersonal, updateLens]);

  // The Personal ↔ Shared lens switch — EVERY shared viewer, fork or not.
  // With no fork yet, selecting "Personal" IS the Personalize entry (arms
  // the fork, adopts a managed board's virtual seeds); with one, the mode
  // change resets the store's working copy (re-seeding from the fork or the
  // canonical rows) and the undo/redo stacks in lockstep.
  const onSwitchLens = useCallback(
    (lens: WidgetViewLens) => {
      if (lens === "personal" && !hasFork) {
        onPersonalize();
        return;
      }
      trackWidget(WidgetViewEvent.SwitchViewLens, { lens });
      updateLens({ lens });
    },
    [hasFork, onPersonalize, trackWidget, updateLens],
  );

  // My Work's reset-to-default: restore the catalog's designed default-on
  // board. Autosave-mode `restoreBoard` diff-commits (removes extras →
  // re-adds the default set at its designed slots), so this reuses the one
  // persistence path rather than growing a new one.
  const onResetToDefault = useCallback(() => {
    trackWidget(WidgetViewEvent.ResetPersonalLayout);
    restoreBoard(managedVirtualInstances(managedCatalog ?? [], registry));
  }, [trackWidget, restoreBoard, managedCatalog, registry]);

  // The Start-over dialog (free dashboards, edit mode): replace the WHOLE
  // board with a template — or clear it to a blank page — through
  // `restoreBoard`, the same mode-routed wholesale-replace undo/redo uses.
  // Staged mode: the replacement stages behind Save-for-team / Save-for-just-
  // me / Discard like any other edit; personal mode: it lands in the fork.
  // A cleared board STAYS blank across reloads — the server's widget_view
  // marker (stamped when the board was first seeded or written) suppresses
  // its lazy default seeding forever after.
  const [startOverOpen, setStartOverOpen] = useState(false);
  const onApplyTemplate = useCallback(
    (template: DashboardTemplate) => {
      trackWidget(WidgetViewEvent.ApplyTemplate, { template: template.key });
      history.snapshot();
      void restoreBoard(templateInstances(template, registry));
      setStartOverOpen(false);
    },
    [trackWidget, history, restoreBoard, registry],
  );
  const onClearBoard = useCallback(() => {
    trackWidget(WidgetViewEvent.ClearBoard);
    history.snapshot();
    void restoreBoard([]);
    setStartOverOpen(false);
  }, [trackWidget, history, restoreBoard]);

  const stagedAudience =
    scope.kind === "team"
      ? `everyone on ${teamMap.get(entityId)?.teamName ?? "the team"}`
      : "everyone who can see this dashboard";
  // The Done dialog's shared-destination anchor — same icon grammar as the
  // ownership pill's Shared state.
  const sharedScopeIcon =
    scope.kind === "workflow" ? ENTITY_KIND_ICON.workflow : ENTITY_KIND_ICON.team;
  // EVERY viewer's whole personalization surface lives in the pill: the
  // Personal ↔ Shared switch is ALWAYS present on a shared view — a forkless
  // viewer's "Personal" side arms Personalize; it never collapses to a
  // one-sided Personalize row or switch. My Work (private-owner) gets the
  // reset-to-default row instead. The switch shows the EFFECTIVE lens, not
  // the persisted preference: a forkless viewer whose stored lens says
  // "personal" is really looking at the shared board until Personalize arms.
  const showLensSwitch = access.tier === "shared";
  const effectiveLens: WidgetViewLens = personalActive ? "personal" : "template";
  const pill = (
    <OwnershipPill
      tier={access.tier}
      canWrite={access.canWrite}
      scopeKind={scope.kind}
      writeGrant={access.writeGrant}
      personalActive={personalActive}
      hasFork={hasFork}
      dirty={dirty}
      stagedAudience={stagedAudience}
      lens={showLensSwitch ? effectiveLens : undefined}
      onSwitchLens={showLensSwitch ? onSwitchLens : undefined}
      onSaveAsPersonal={
        access.tier === "shared" && access.canWrite && !hasFork ? onSaveAsPersonal : undefined
      }
      onResetPersonal={onResetPersonal}
      onDiscardStaged={onDiscardStaged}
      onSaveForTeam={onSaveForTeam}
      onResetToDefault={
        access.tier === "private-owner" && managedCatalog ? onResetToDefault : undefined
      }
    />
  );

  // ONE gear on the masthead: it opens the Manage-widgets side panel, which
  // also hosts the page-chrome settings (the panel's `settings` slot below) —
  // never a second trigger beside it.
  const mastheadActions =
    canEdit || pageSettingsState ? (
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Manage widgets"
        onClick={() => setAddOpen(true)}
      >
        <Icon name="settings" size="md" />
      </Button>
    ) : undefined;

  const onDateRangeChange = useCallback(
    (next: typeof scopeState.dateRange) => {
      scopeState.setDateRange(next);
      trackWidget(WidgetViewEvent.FilterDate, { preset: next.key });
    },
    [scopeState, trackWidget],
  );

  // Wait for the view's widgets to load once before painting the grid, so a
  // shared dashboard doesn't flash empty + an "add your first widget" CTA
  // before the server's widgets arrive. Full-page ONLY on a view's FIRST
  // load: later `initialLoading` flips are transient working-copy resets
  // (the lens switch, Personalize, Reset), and unmounting the chrome there
  // would rip the open pill popover out from under the click — those keep
  // the chrome and spin the board area below instead.
  if (!initialLoading) hasLoadedViewRef.current = access.view;
  if (initialLoading && hasLoadedViewRef.current !== access.view) {
    return (
      <Box align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  return (
    <DashboardScopeProvider value={scopeState.scope}>
      <WidgetDrilldownProvider value={drilldown}>
        {/* Header chromes paint `background` themselves; the board below
          is the recessed `sunken` well so widget cards read raised. */}
        <Box direction="column" fill="flex" background="sunken">
          {isManaged ? (
            title ? (
              <DashboardMastheadChrome title={title} pill={pill} actions={mastheadActions} />
            ) : (
              <ManagedToolbarChrome
                pill={pill}
                dateRange={scopeState.dateRange}
                onDateRangeChange={onDateRangeChange}
                canEdit={canEdit}
                onManageWidgets={() => setAddOpen(true)}
              />
            )
          ) : widgets.length > 0 || initialLoading || dirty ? (
            // `initialLoading` here is always a transient working-copy reset
            // (first load full-page-returned above): keep the toolbar — and
            // the open pill popover in it — mounted across the lens switch.
            // `dirty` keeps it mounted when the board is cleared to blank —
            // the pill's save decision must stay reachable.
            <FreeToolbarChrome
              pill={pill}
              dateRange={scopeState.dateRange}
              onDateRangeChange={onDateRangeChange}
              onlyMeSelected={scopeState.currentUser.isCurrentUserButtonSelected}
              onOnlyMePress={() => {
                // Mirror the toggle's own resolution so analytics reports the
                // state the press actually lands on (desktop always selects;
                // touch toggles), not a hardcoded `true`.
                const enabled = scopeState.currentUser.isDesktop
                  ? true
                  : !scopeState.currentUser.isCurrentUserButtonSelected;
                scopeState.currentUser.handleCurrentUserPress();
                trackWidget(WidgetViewEvent.ToggleOnlyMe, { enabled });
              }}
              onOnlyMeClear={() => {
                scopeState.currentUser.handleCurrentUserClear();
                trackWidget(WidgetViewEvent.ToggleOnlyMe, { enabled: false });
              }}
              onOpenFilters={issueFilters ? issueFilters.toggleFilterPanel : undefined}
              filterCount={issueFilters?.filters.activeFilterCount ?? 0}
              onClearFilters={issueFilters ? issueFilters.filters.clearAll : undefined}
              canEdit={canEdit}
              history={history}
              onTidy={onTidy}
              onAddWidget={() => setAddOpen(true)}
              onStartOver={() => setStartOverOpen(true)}
            />
          ) : null}

          {/* The board's own scroller — its scrollbar is user-visible chrome, and
            the grid finds it by walking up to the nearest overflow ancestor
            (`getScrollParent`), so ScrollArea's viewport serves it directly. */}
          <Box direction="column" grow minHeight={0}>
            <ScrollArea scrollFade>
            <Box paddingTop="md" paddingX="md" paddingBottom="xl" gap="md" minHeight="100%">
              {pageSettingsState?.showGreeting ? (
                <Box direction="column" gap="2xs">
                  <Text size="2xl" weight="bold">
                    {greetingLabel(currentUser?.name)}
                  </Text>
                  <Text size="sm" variant="muted">
                    Today’s date is {todayDateLabel()}.
                  </Text>
                </Box>
              ) : null}
              {initialLoading ? (
                // Transient working-copy reset (lens switch / Personalize /
                // Reset — the first load full-page-returns above): the board
                // area spins while the chrome around it stays mounted.
                <Box paddingY="2xl" align="center">
                  <Spinner />
                </Box>
              ) : widgets.length === 0 ? (
                // An empty board is always DELIBERATE now — the server seeds
                // a new view's default board on its first read, so this state
                // only exists after someone cleared it (or emptied their
                // personal fork). Editors get the template chooser back —
                // exactly the "pick a starting point" moment a cleared board
                // asks for; viewers get the honest empty state.
                canEdit && !isManaged ? (
                  <DashboardTemplateChooser
                    onSelectTemplate={(seeds) => {
                      history.snapshot();
                      seedWidgets(seeds);
                    }}
                    onStartFromScratch={() => setAddOpen(true)}
                  />
                ) : (
                  <Box direction="column" paddingY="2xl">
                    <WidgetEmpty
                      size="default"
                      icon="layout-dashboard"
                      title="No widgets yet"
                      description="This dashboard doesn't have any widgets yet."
                    />
                  </Box>
                )
              ) : (
                <WidgetGrid
                  instances={widgets}
                  // Free-board tiles float over the sunken board the same as
                  // managed ones — one look for every dashboard.
                  surface="elevated"
                  // The board is the recessed `sunken` well, so a canvas-pinned
                  // card must fill `sunken` to mask the authoring guide dots.
                  canvas="sunken"
                  canManage={gridCanManage}
                  canArrange={canEdit}
                  widthOptions={canEdit && halfFullWidths ? HALF_FULL_WIDTH_OPTIONS : undefined}
                  onMove={onMove}
                  onLayoutChange={isManaged ? undefined : history.handleLayoutChange}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onReplace={isManaged ? undefined : onReplace}
                  onMaximize={onMaximize}
                  onConfigure={onConfigure}
                  onRename={(id, title) => onConfigChange(id, { title })}
                  // Body-level config persistence (a note's content, a column
                  // resize) is an edit — only wire it when the viewer may edit
                  // this board. A read-only viewer gets `undefined`, which is
                  // also THE signal an inline-editable body (the note) reads to
                  // render read-only. My Work's owner is a writer (private-owner
                  // needs no `widgets.manage`), so their note stays editable.
                  onConfigChange={canEdit ? onConfigChange : undefined}
                  areaAdd={isManaged ? undefined : areaAdd}
                  renderCard={isManaged ? renderManagedCard : undefined}
                />
              )}
            </Box>
            </ScrollArea>
          </Box>

          <WidgetDetailDialog
            instance={maximizedInstance}
            open={maximizedInstance !== null}
            // The RIGHT, not the mode: maximizing a widget to tweak its
            // settings is a deliberate act and needs no board edit mode.
            canManage={canManageWidgets}
            onOpenChange={(o) => {
              if (!o) {
                setMaximizedId(null);
                setDataSlice(null);
              }
            }}
            onPrevious={
              maximizedIndex > 0
                ? () => {
                    setDataSlice(null);
                    setMaximizedId(at(widgets, maximizedIndex - 1, "widget").id);
                  }
                : undefined
            }
            onNext={
              maximizedIndex >= 0 && maximizedIndex < widgets.length - 1
                ? () => {
                    setDataSlice(null);
                    setMaximizedId(at(widgets, maximizedIndex + 1, "widget").id);
                  }
                : undefined
            }
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onConfigChange={onConfigChange}
            dataSlice={dataSlice}
            dataRequestId={dataRequestId}
            onClearDataSlice={() => setDataSlice(null)}
            settingsRequestId={settingsRequestId}
          />
        </Box>
      </WidgetDrilldownProvider>

      {!managedCatalog ? (
        <DashboardStartOverDialog
          open={startOverOpen}
          onOpenChange={setStartOverOpen}
          onSelectTemplate={onApplyTemplate}
          onClearBoard={onClearBoard}
        />
      ) : null}

      {managedCatalog ? (
        <ManageWidgetsPanel
          open={addOpen}
          onOpenChange={setAddOpen}
          catalog={managedCatalog}
          instances={widgets}
          onToggle={onToggleManaged}
          settings={
            pageSettingsState && onPageSettingsUpdate ? (
              <DashboardPageSettingsSection
                settings={pageSettingsState}
                onUpdate={onPageSettingsUpdate}
              />
            ) : undefined
          }
        />
      ) : (
        <AddWidgetDialog
          open={addOpen || replaceId !== null}
          onOpenChange={(o) => {
            if (o) return;
            setAddOpen(false);
            setReplaceId(null);
            setMarqueeArea(null);
          }}
          title={replaceId ? "Replace Widget" : undefined}
          onSelect={(preset) => {
            if (replaceId) {
              trackWidget(WidgetViewEvent.ReplaceWidget, {
                from: widgets.find((i) => i.id === replaceId)?.type,
                to: preset.type,
              });
              history.snapshot();
              replaceWidget(replaceId, preset);
              setReplaceId(null);
            } else {
              // A marquee-opened gallery places the pick at the drawn rect;
              // the toolbar-opened one lands in the first free slot.
              trackWidget(WidgetViewEvent.AddWidget, {
                type: preset.type,
                ...(marqueeArea ? { via: "marquee" } : {}),
              });
              history.snapshot();
              addWidget(preset, marqueeArea ?? undefined);
              setMarqueeArea(null);
            }
            setAddOpen(false);
          }}
        />
      )}

    </DashboardScopeProvider>
  );
}
